import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Loader2, CreditCard, Lock, CheckCircle, AlertCircle, AlertTriangle, Check, Truck, MapPin } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { checkoutAPI } from '../services/api';
import { MAX_CART_ITEMS, resolveProductId, toCheckoutLine } from '../config/cartContract';
/**
 * BCW Refund Request Module v1.0.0
 * Check if a product is a collectible (non-refundable)
 * BCW Supplies = Refundable, Collectibles = FINAL SALE
 */
function isCollectible(item) {
  const category = (item.category || '').toLowerCase();
  const source = (item.source || '').toLowerCase();

  // BCW supplies are NOT collectibles (they're refundable)
  if (source === 'bcw' || category === 'supplies' || category === 'bcw supplies' || category === 'bcw') {
    return false;
  }

  // Everything else (comics, graded) is a collectible
  return true;
}



// Payment form component (inside Elements provider)
// P1-5: Removed token prop - auth handled via HttpOnly cookies
function PaymentForm({ clientSecret, cartItems, amountCents, onSuccess, onCancel, hasCollectibles, acknowledged, setAcknowledged, hasBcwItems }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // ⛔ AN ALREADY-OVER-LONG CART CANNOT BE FIXED BY BLOCKING ADDS. A cart stored
    // before the cap existed, or synced from another device, can hold more than the
    // contract allows. It LOADS IN FULL -- silently trimming it would destroy items the
    // buyer chose -- and is refused here with a message that says exactly what to do.
    // Never a bare 422: "Input should have at most 50 items" tells a buyer nothing.
    if (cartItems.length > MAX_CART_ITEMS) {
      setError(
        `Your cart has ${cartItems.length} items; the maximum is ${MAX_CART_ITEMS}. ` +
          `Remove ${cartItems.length - MAX_CART_ITEMS} to continue.`
      );
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.origin + '/order-complete',
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment successful - create order in our database
        // P1-5: No token needed - auth via cookies
        const orderResult = await checkoutAPI.confirmOrder(
          null,  // P1-5: token no longer needed
          paymentIntent.id,
          // ⛔ toCheckoutLine, NOT a hand-built {product_id}. A bundle line carries a BUNDLE
          // id, and sending it as product_id charges for whatever product holds that id.
          // One function decides which field a line uses, so the two payload builders in
          // this file cannot disagree.
          cartItems.map(toCheckoutLine).filter(Boolean)
        );
        onSuccess(orderResult);
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-white">Payment Details</h3>
        </div>

        {/* Stripe Payment Element - only card info goes to Stripe */}
        <div className="bg-zinc-900 rounded-lg p-4">
          <PaymentElement
            options={{
              layout: 'tabs',
            }}
          />
        </div>

        <div className="flex items-center gap-2 mt-4 text-xs text-zinc-500">
          <Lock className="w-3 h-3" />
          <span>Your payment info is encrypted and secure</span>
        </div>
      </div>

      
      {/* Final Sale Acknowledgment - BCW Refund Module v1.0.0 */}
      {hasCollectibles && (
        <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-semibold text-orange-400 mb-1">Final Sale Notice</h4>
              <p className="text-sm text-zinc-400 mb-3">
                Your cart contains collectible items (comics or graded items).
                Due to the condition-sensitive nature of collectibles, <strong className="text-orange-400">all sales are final</strong> and 
                not eligible for returns or refunds.
              </p>
              <label className="flex items-start gap-3 cursor-pointer group">
                <div 
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    acknowledged 
                      ? 'bg-orange-500 border-orange-500' 
                      : 'border-zinc-600 group-hover:border-orange-500'
                  }`}
                  onClick={() => setAcknowledged(!acknowledged)}
                >
                  {acknowledged && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="text-sm text-zinc-300 select-none" onClick={() => setAcknowledged(!acknowledged)}>
                  I understand that collectible items are <strong>FINAL SALE</strong> and not eligible for returns or refunds. 
                  I have reviewed the product descriptions and images before purchase.
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Order Summary with BCW badges */}
      {hasBcwItems && (
        <div className="bg-zinc-800 rounded-xl p-4 space-y-3">
          <h4 className="text-sm font-semibold text-white mb-2">Order Summary</h4>
          {cartItems.map((item, idx) => {
            /* c8 ignore next */
            const isBcw = (item.source || '').toLowerCase() === 'bcw' || (item.category || '').toLowerCase() === 'supplies';
            return (
              <div key={item.id || idx} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-300">{item.name} x{item.quantity}</span>
                  {isBcw && (
                    <span className="inline-flex items-center gap-1 text-xs text-blue-400 bg-blue-400/10 px-2 py-0.5 rounded-full">
                      <Truck className="w-3 h-3" />
                      Ships from supplier
                    </span>
                  )}
                </div>
                {/* c8 ignore next */}
                <span className="text-zinc-400">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            );
          })}
          <div className="border-t border-zinc-700 pt-2 flex justify-between text-sm">
            <span className="text-zinc-400">BCW Shipping</span>
            <span className="text-zinc-300">Included in total</span>
          </div>
        </div>
      )}

      {/* Address warnings for BCW items */}
      {hasBcwItems && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-amber-400/80 space-y-1">
              <p>BCW supply items ship from a third-party supplier. PO Box addresses may not be eligible for delivery.</p>
              <p>Shipping to Alaska, Hawaii, and US territories may have limited options or additional fees.</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="flex-1 py-3 bg-zinc-700 text-white rounded-xl font-semibold hover:bg-zinc-600 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing || (hasCollectibles && !acknowledged)}
          className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>Pay ${(amountCents / 100).toFixed(2)}</>
          )}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Shipping address -- collected BEFORE the PaymentIntent exists
// ---------------------------------------------------------------------------
//
// ⛔⛔ S11b/S11c. THE ORDER OF THESE TWO STEPS IS THE WHOLE FIX. This form used to not
// exist: the component mounted, immediately called create-payment-intent, and an order
// was created with NO DESTINATION. `orders_ever = 0` on production is the only reason
// nothing shipped into a void.
//
// The address is collected here because the backend persists it AT INTENT CREATION, not
// at confirm-order. That is not a preference -- the 3DS path never returns to
// confirm-order at all, the Stripe webhook creates the order instead, and the webhook has
// nothing but the intent to read. An address collected after this point cannot reach the
// order that the webhook builds.
export const EMPTY_ADDRESS = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
};

// ⛔ line2 IS THE ONLY OPTIONAL FIELD. Everything else is what a carrier needs to
// deliver; a blank city is not a smaller defect than a blank street.
const REQUIRED_ADDRESS_FIELDS = ['name', 'line1', 'city', 'state', 'postal_code'];

export function missingAddressFields(address) {
  return REQUIRED_ADDRESS_FIELDS.filter((f) => !(address[f] || '').trim());
}

const ADDRESS_LABELS = {
  name: 'Full name',
  line1: 'Street address',
  line2: 'Apt, suite, unit (optional)',
  city: 'City',
  state: 'State',
  postal_code: 'ZIP code',
  country: 'Country',
};

// ⛔ NO `submitting` PROP. The first version took one and rendered a spinner branch that
// COULD NEVER EXECUTE: the parent returns "Preparing checkout..." while `loading` is
// true, so this form is only ever mounted with loading false. Coverage found it as an
// unreachable branch, and an unreachable branch is dead code, not a coverage target.
export function ShippingAddressForm({ onSubmit, onCancel }) {
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [touched, setTouched] = useState(false);

  const missing = missingAddressFields(address);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (missing.length > 0) {
      return;
    }
    // Trim on the way out: a trailing space in a postal code is a carrier's problem.
    // ⛔ NO `typeof v === 'string'` GUARD. Every value in this state object is a string --
    // EMPTY_ADDRESS is all strings and the only writer is `e.target.value`. The guard's
    // false branch was unreachable, which coverage reported as a partial branch. An
    // unreachable defence is not defence in depth; it is a claim nobody can check.
    const cleaned = Object.fromEntries(Object.entries(address).map(([k, v]) => [k, v.trim()]));
    onSubmit({ ...cleaned, line2: cleaned.line2 || null });
  };

  const field = (key, extraClass = '') => (
    <div className={extraClass}>
      <label htmlFor={`ship-${key}`} className="block text-sm text-zinc-400 mb-1">
        {ADDRESS_LABELS[key]}
      </label>
      <input
        id={`ship-${key}`}
        name={key}
        type="text"
        value={address[key]}
        onChange={(e) => setAddress((a) => ({ ...a, [key]: e.target.value }))}
        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
      />
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-zinc-800 rounded-xl p-6 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-white">Shipping Address</h3>
        </div>

        {field('name')}
        {field('line1')}
        {field('line2')}
        <div className="grid grid-cols-2 gap-4">
          {field('city')}
          {field('state')}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {field('postal_code')}
          {field('country')}
        </div>

        <p className="text-xs text-zinc-500">
          Shipping is calculated from your cart on the next step.
        </p>
      </div>

      {touched && missing.length > 0 && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>
            Please complete: {missing.map((f) => ADDRESS_LABELS[f]).join(', ')}
          </span>
        </div>
      )}

      <div className="flex gap-4">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-3 bg-zinc-700 text-white rounded-xl font-semibold hover:bg-zinc-600 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          Continue to Payment
        </button>
      </div>
    </form>
  );
}

// Main checkout component
// P1-5: Removed token prop - auth handled via HttpOnly cookies
// OPT-005: Added cleanup to cancel reservation on unmount
export default function CheckoutForm({ cartItems, onSuccess, onCancel, onQuote }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  // ⛔ S11c. THE SERVER'S BREAKDOWN, HELD VERBATIM. `total` used to arrive as a PROP the
  // caller computed with `cartTotal >= 50 ? 0 : 5.99` -- a policy written down nowhere --
  // and the Pay button rendered it while Stripe charged something else entirely. There is
  // no prop any more: what is displayed is what create-payment-intent returned, which is
  // the same integer the charge was raised for.
  const [quote, setQuote] = useState(null);
  // ⚠ STARTS false, NOT true. Nothing loads until the buyer submits an address, because
  // the intent cannot be created without one.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // BCW Refund Module v1.0.0: Final sale acknowledgment for collectibles
  const [acknowledged, setAcknowledged] = useState(false);
  const hasCollectibles = useMemo(() => cartItems.some(isCollectible), [cartItems]);
  const hasBcwItems = useMemo(() => cartItems.some(item => {
    const source = (item.source || '').toLowerCase();
    const category = (item.category || '').toLowerCase();
    return source === 'bcw' || category === 'supplies';
  }), [cartItems]);

  // OPT-005: Track payment intent ID and order completion for cleanup
  const paymentIntentIdRef = useRef(null);
  const orderCompletedRef = useRef(false);

  // OPT-005: Cleanup function to cancel reservation
  const cancelReservation = useCallback(async () => {
    if (paymentIntentIdRef.current && !orderCompletedRef.current) {
      try {
        await checkoutAPI.cancelReservation(paymentIntentIdRef.current);
        console.log('Reservation cancelled successfully');
      } catch (err) {
        // ⛔ THIS SWALLOW IS WHY THE 422 LIVED FOR SO LONG. The route could NEVER
        // succeed (a bare scalar bound as a query param against a JSON body), and
        // console.debug is invisible at default log levels, so a guaranteed failure
        // read exactly like a success on every abandoned checkout.
        //
        // ⚠ IT STAYS NON-BLOCKING ON PURPOSE. This runs on UNMOUNT, so there is no
        // component left to render an error into, and the TTL sweeper really is a
        // genuine backstop. What changes is that the failure is now OBSERVABLE:
        // console.error plus a Sentry breadcrumb, so a regression surfaces in
        // telemetry instead of hiding until inventory reads zero with no sales.
        console.error('Failed to cancel stock reservation:', err);
        Sentry.captureException(err, {
          tags: { surface: 'checkout', operation: 'cancel_reservation' },
          extra: { paymentIntentId: paymentIntentIdRef.current },
        });
      }
    }
  }, []);

  // OPT-005: Cancel reservation on unmount
  useEffect(() => {
    return () => {
      cancelReservation();
    };
  }, [cancelReservation]);

  // Wrap onSuccess to mark order as completed
  const handleSuccess = useCallback((order) => {
    orderCompletedRef.current = true;
    onSuccess(order);
  }, [onSuccess]);

  // ⛔⛔ NO `useEffect(initializeCheckout, [])` ANY MORE. Creating the intent on mount is
  // exactly how an order got created with no destination: there was nowhere in the flow
  // for an address to be collected before the money was reserved. The intent is now
  // created by an explicit act -- submitting the address -- and never before.
  const beginPayment = async (shippingAddress) => {
    // MED-005: Reset error state at start to clear stale errors
    setError(null);
    setLoading(true);
    try {
      // Get Stripe publishable key from our backend
      const config = await checkoutAPI.getConfig();
      setStripePromise(loadStripe(config.publishable_key));

      // Create payment intent
      // Same single decision point as the confirm payload above.
      const items = cartItems.map(toCheckoutLine).filter(Boolean);

      // P1-5: No token needed - auth via cookies
      const paymentIntent = await checkoutAPI.createPaymentIntent(null, items, shippingAddress);
      setClientSecret(paymentIntent.client_secret);

      // ⭐ THE SERVER'S NUMBERS, LIFTED ONCE. `amount` is the integer Stripe was asked to
      // charge; the parts are what it is made of. The order summary and the Pay button
      // both read THIS, so they cannot disagree with each other or with the charge.
      const serverQuote = {
        subtotal_cents: paymentIntent.subtotal_cents,
        shipping_cents: paymentIntent.shipping_cents,
        tax_cents: paymentIntent.tax_cents,
        shipping_tier: paymentIntent.shipping_tier,
        total_cents: paymentIntent.amount,
      };
      setQuote(serverQuote);
      if (onQuote) {
        onQuote(serverQuote);
      }

      // OPT-005: Store payment intent ID for cleanup
      // Extract from client_secret format: pi_xxx_secret_yyy
      const piId = paymentIntent.client_secret?.split('_secret_')[0];
      paymentIntentIdRef.current = piId;
    } catch (err) {
      setError(err.message || 'Failed to initialize checkout');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
        <p className="text-zinc-400">Preparing checkout...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-400 mb-4">{error}</p>
        <button
          onClick={onCancel}
          className="px-6 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600"
        >
          Go Back
        </button>
      </div>
    );
  }

  // ⛔ STEP ONE. Until an address exists there is no intent, no client secret and nothing
  // to pay with. This used to `return null` -- a blank panel while the mount-time intent
  // was in flight -- which is why there was never anywhere to put an address.
  if (!clientSecret || !stripePromise || !quote) {
    return <ShippingAddressForm onSubmit={beginPayment} onCancel={onCancel} />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#f97316',
            colorBackground: '#18181b',
            colorText: '#ffffff',
            colorDanger: '#ef4444',
            fontFamily: 'system-ui, sans-serif',
            borderRadius: '8px',
          },
        },
      }}
    >
      <PaymentForm
        clientSecret={clientSecret}
        cartItems={cartItems}
        amountCents={quote.total_cents}
        onSuccess={handleSuccess}
        onCancel={onCancel}
        hasCollectibles={hasCollectibles}
        acknowledged={acknowledged}
        setAcknowledged={setAcknowledged}
        hasBcwItems={hasBcwItems}
      />
    </Elements>
  );
}

// Order success component
export function OrderSuccess({ order, onClose }) {
  return (
    <div className="text-center py-8">
      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-white mb-2">Order Confirmed!</h2>
      <p className="text-zinc-400 mb-4">
        Thank you for your purchase.
      </p>
      <div className="bg-zinc-800 rounded-xl p-4 mb-6 inline-block">
        <p className="text-sm text-zinc-400">Order Number</p>
        <p className="text-lg font-mono font-semibold text-orange-500">
          {order.order_number}
        </p>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        A confirmation email will be sent to your registered email address.
      </p>
      <button
        onClick={onClose}
        className="px-8 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600"
      >
        Continue Shopping
      </button>
    </div>
  );
}
